import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Table,
  Row,
  Col,
  Alert,
  Tabs,
  Tab,
  InputGroup,
  Badge,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { doc, getDoc, setDoc, getFirestore } from "firebase/firestore";
import { 
  FaSave, 
  FaUndo, 
  FaInfoCircle, 
  FaCog,
  FaUsers,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaUmbrella,
  FaHardHat,
  FaExclamationTriangle,
  FaCheck,
  FaLandmark
} from "react-icons/fa";
import { 
  BsShieldCheck, 
  BsCalendar3, 
  BsFileText,
  BsGear,
  BsAward
} from "react-icons/bs";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";

const db = getFirestore(firebaseApp);

const REGIMENES = {
  GENERAL: {
    titulo: "Régimen General (D.Leg. 728)",
    descripcion: "Aplicable a empresas privadas en general sin importar su tamaño.",
    icono: <FaUsers />,
    color: "#8B4513"
  },
  MYPE_MICRO: {
    titulo: "Microempresa (REMYPE)",
    descripcion: "Para empresas con ventas anuales hasta 150 UIT.",
    icono: <BsGear />,
    color: "#28a745"
  },
  MYPE_PEQUENA: {
    titulo: "Pequeña empresa (REMYPE)",
    descripcion: "Para empresas con ventas anuales de 150 UIT hasta 1,700 UIT.",
    icono: <BsAward />,
    color: "#007bff"
  },
  AGRARIO: {
    titulo: "Régimen Agrario (Ley 31110)",
    descripcion: "Aplicable a empresas agrarias y agroexportadoras.",
    icono: <FaUmbrella />,
    color: "#28a745"
  },
  CONSTRUCCION_CIVIL: {
    titulo: "Construcción Civil",
    descripcion: "Aplicable a trabajadores de construcción civil.",
    icono: <FaHardHat />,
    color: "#fd7e14"
  },
  CAS: {
    titulo: "CAS (D.Leg. 1057)",
    descripcion: "Aplicable a entidades públicas (en proceso de eliminación).",
    icono: <FaLandmark />,
    color: "#6f42c1"
  },
};

const ConfiguracionRegimen = ({ usuario }) => {
  const [ruc, setRuc] = useState(usuario.ruc);
  const [config, setConfig] = useState({
    regimen_principal: "GENERAL",
    parametros: {
      GENERAL: {
        gratificacion: { factor: 1, meses: [7, 12] },
        cts: { factor: 1, meses: [5, 11] },
        vacaciones: { dias: 30 },
        asignacion_familiar: { aplica: true, porcentaje_rmv: 10 },
        indemnizacion: { factor_por_anio: 1.5, tope_anios: 12 },
      },
      MYPE_MICRO: {
        gratificacion: { factor: 0, aplica: false },
        cts: { factor: 0, aplica: false },
        vacaciones: { dias: 15 },
        asignacion_familiar: { aplica: false },
        indemnizacion: { factor_por_anio: 0.33, tope_dias: 90 },
      },
      MYPE_PEQUENA: {
        gratificacion: { factor: 0.5, meses: [7, 12] },
        cts: { factor: 0.5, meses: [5, 11] },
        vacaciones: { dias: 15 },
        asignacion_familiar: { aplica: false },
        indemnizacion: { factor_por_anio: 0.66, tope_dias: 120 },
      },
      AGRARIO: {
        gratificacion: { factor: 0, incluida_en_diario: true },
        cts: { factor: 0, incluida_en_diario: true },
        vacaciones: { dias_min: 15, dias_max: 30, escala_progresiva: true },
        essalud: { porcentaje: 9 },
      },
      CONSTRUCCION_CIVIL: {
        salarios: {
          operario: { factor_jornal: 1 },
          oficial: { factor_jornal: 0.8 },
          peon: { factor_jornal: 0.7 },
        },
        bonificaciones: {
          altura: { porcentaje: 7 },
          altitud: { mayor_3000msnm: 10, mayor_4000msnm: 20 },
          agua: { porcentaje: 20 },
          toxicidad: { porcentaje: 10 },
        },
      },
      CAS: {
        gratificacion: { aguinaldo: true, montos_fijos: true },
        cts: { aplica: false },
        vacaciones: { dias: 30 },
      },
    },
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [esConfiguracionNueva, setEsConfiguracionNueva] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    if (ruc) {
      cargarConfiguracion();
    } else {
      setLoading(false);
    }
  }, [ruc]);

  const cargarConfiguracion = async () => {
    try {
      setLoading(true);
      const configRef = doc(db, "pl_regimenes_laborales", ruc);
      const configDoc = await getDoc(configRef);

      if (configDoc.exists()) {
        const datosConfig = configDoc.data();
        
        // Convertir timestamps si existen
        if (datosConfig.updated_at) {
          datosConfig.updated_at = datosConfig.updated_at.toDate();
          setLastSaved(datosConfig.updated_at);
        }
        
        setConfig((prev) => ({
          ...prev,
          ...datosConfig,
        }));
        setEsConfiguracionNueva(false);
      } else {
        console.log("No se encontró configuración de regímenes previa.");
      }
    } catch (error) {
      console.error("Error al cargar configuración de regímenes:", error);

      if (error.code !== "not-found") {
        Swal.fire({
          icon: "warning",
          title: "Aviso",
          text: "Se ha cargado la configuración por defecto de regímenes laborales.",
          confirmButtonColor: "#8B4513"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGuardarConfiguracion = async () => {
    setSaving(true);
    try {
      if (!ruc) {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se puede guardar la configuración sin un RUC de empresa válido",
          confirmButtonColor: "#8B4513"
        });
        return;
      }

      // Validar la configuración según el régimen principal
      const regimenPrincipal = config.regimen_principal;
      const parametrosRegimen = config.parametros[regimenPrincipal];

      // Validaciones específicas según régimen
      let mensajeError = "";
      switch (regimenPrincipal) {
        case "GENERAL":
          if (parametrosRegimen.gratificacion.factor !== 1) {
            mensajeError = "El factor de gratificación debe ser 1 para el Régimen General";
          }
          break;
        case "MYPE_PEQUENA":
          if (parametrosRegimen.gratificacion.factor !== 0.5) {
            mensajeError = "El factor de gratificación debe ser 0.5 para la Pequeña Empresa";
          }
          break;
        case "MYPE_MICRO":
          if (parametrosRegimen.gratificacion.aplica !== false) {
            mensajeError = "Las gratificaciones no aplican para la Microempresa";
          }
          break;
      }

      if (mensajeError) {
        await Swal.fire({
          icon: "error",
          title: "Error de validación",
          text: mensajeError,
          confirmButtonColor: "#8B4513"
        });
        return;
      }

      const configActualizada = {
        ...config,
        ruc_empresa: ruc,
        updated_at: new Date(),
        created_at: esConfiguracionNueva ? new Date() : config.created_at || new Date(),
      };

      const configRef = doc(db, "pl_regimenes_laborales", ruc);
      await setDoc(configRef, configActualizada);

      setConfig(configActualizada);
      setEsConfiguracionNueva(false);
      setLastSaved(new Date());

      await Swal.fire({
        icon: "success",
        title: "Configuración guardada",
        text: "La configuración de regímenes laborales se ha actualizado correctamente",
        confirmButtonColor: "#8B4513",
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error("Error al guardar:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo guardar la configuración: " + (error.message || "Error desconocido"),
        confirmButtonColor: "#8B4513"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResetearConfiguracion = () => {
    Swal.fire({
      title: "¿Restablecer configuración?",
      text: "Se cargarán los valores predeterminados para todos los regímenes",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#8B4513",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, restablecer",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        // Reiniciar configuración por defecto
        setConfig({
          regimen_principal: "GENERAL",
          parametros: {
            GENERAL: {
              gratificacion: { factor: 1, meses: [7, 12] },
              cts: { factor: 1, meses: [5, 11] },
              vacaciones: { dias: 30 },
              asignacion_familiar: { aplica: true, porcentaje_rmv: 10 },
              indemnizacion: { factor_por_anio: 1.5, tope_anios: 12 },
            },
            MYPE_MICRO: {
              gratificacion: { factor: 0, aplica: false },
              cts: { factor: 0, aplica: false },
              vacaciones: { dias: 15 },
              asignacion_familiar: { aplica: false },
              indemnizacion: { factor_por_anio: 0.33, tope_dias: 90 },
            },
            MYPE_PEQUENA: {
              gratificacion: { factor: 0.5, meses: [7, 12] },
              cts: { factor: 0.5, meses: [5, 11] },
              vacaciones: { dias: 15 },
              asignacion_familiar: { aplica: false },
              indemnizacion: { factor_por_anio: 0.66, tope_dias: 120 },
            },
            AGRARIO: {
              gratificacion: { factor: 0, incluida_en_diario: true },
              cts: { factor: 0, incluida_en_diario: true },
              vacaciones: {
                dias_min: 15,
                dias_max: 30,
                escala_progresiva: true,
              },
              essalud: { porcentaje: 9 },
            },
            CONSTRUCCION_CIVIL: {
              salarios: {
                operario: { factor_jornal: 1 },
                oficial: { factor_jornal: 0.8 },
                peon: { factor_jornal: 0.7 },
              },
              bonificaciones: {
                altura: { porcentaje: 7 },
                altitud: { mayor_3000msnm: 10, mayor_4000msnm: 20 },
                agua: { porcentaje: 20 },
                toxicidad: { porcentaje: 10 },
              },
            },
            CAS: {
              gratificacion: { aguinaldo: true, montos_fijos: true },
              cts: { aplica: false },
              vacaciones: { dias: 30 },
            },
          },
        });

        Swal.fire({
          icon: "info",
          title: "Configuración restablecida",
          text: "Se han cargado los valores predeterminados. Recuerde guardar los cambios.",
          confirmButtonColor: "#8B4513"
        });
      }
    });
  };

  const handleChangeRegimenPrincipal = (e) => {
    const nuevoRegimen = e.target.value;

    // Verificar si ya existe configuración para este régimen
    if (!config.parametros[nuevoRegimen]) {
      // Crear configuración básica para el régimen
      const configuracionBasica = getConfiguracionPorDefecto(nuevoRegimen);

      setConfig((prev) => ({
        ...prev,
        regimen_principal: nuevoRegimen,
        parametros: {
          ...prev.parametros,
          [nuevoRegimen]: configuracionBasica,
        },
      }));
    } else {
      // Si ya existe, solo cambiar el régimen principal
      setConfig((prev) => ({
        ...prev,
        regimen_principal: nuevoRegimen,
      }));
    }

    setActiveTab("general");
  };

  // Función para obtener la configuración por defecto según el régimen
  const getConfiguracionPorDefecto = (regimen) => {
    switch (regimen) {
      case "GENERAL":
        return {
          gratificacion: { factor: 1, meses: [7, 12] },
          cts: { factor: 1, meses: [5, 11] },
          vacaciones: { dias: 30 },
          asignacion_familiar: { aplica: true, porcentaje_rmv: 10 },
          indemnizacion: { factor_por_anio: 1.5, tope_anios: 12 },
        };
      case "MYPE_MICRO":
        return {
          gratificacion: { factor: 0, aplica: false },
          cts: { factor: 0, aplica: false },
          vacaciones: { dias: 15 },
          asignacion_familiar: { aplica: false },
          indemnizacion: { factor_por_anio: 0.33, tope_dias: 90 },
        };
      // Añadir casos para los demás regímenes...
      default:
        return {};
    }
  };

  const handleChangeParametro = (regimen, grupo, campo, valor) => {
    let valorFormateado = valor;

    // Convertir a número si es un campo numérico
    if (
      [
        "factor",
        "dias",
        "porcentaje",
        "porcentaje_rmv",
        "factor_por_anio",
        "tope_anios",
        "tope_dias",
      ].includes(campo)
    ) {
      valorFormateado = parseFloat(valor);
    }

    // Convertir a booleano si es un checkbox
    if (
      [
        "aplica",
        "incluida_en_diario",
        "escala_progresiva",
        "aguinaldo",
        "montos_fijos",
      ].includes(campo)
    ) {
      valorFormateado = valor === true || valor === "true";
    }

    setConfig((prev) => {
      // Si es un campo anidado (como bonificaciones.altura.porcentaje)
      if (campo.includes(".")) {
        const [subcampo, propiedad] = campo.split(".");
        return {
          ...prev,
          parametros: {
            ...prev.parametros,
            [regimen]: {
              ...prev.parametros[regimen],
              [grupo]: {
                ...prev.parametros[regimen][grupo],
                [subcampo]: {
                  ...prev.parametros[regimen][grupo][subcampo],
                  [propiedad]: valorFormateado,
                },
              },
            },
          },
        };
      }

      // Si es un campo directo
      return {
        ...prev,
        parametros: {
          ...prev.parametros,
          [regimen]: {
            ...prev.parametros[regimen],
            [grupo]: {
              ...prev.parametros[regimen][grupo],
              [campo]: valorFormateado,
            },
          },
        },
      };
    });
  };

  // Calcular estadísticas de la configuración
  const calcularEstadisticas = () => {
    const totalRegimenes = Object.keys(REGIMENES).length;
    const regimenActivo = config.regimen_principal;
    const parametrosCompletos = config.parametros[regimenActivo] ? 
      Object.keys(config.parametros[regimenActivo]).length : 0;
    
    return {
      totalRegimenes,
      regimenActivo,
      parametrosCompletos
    };
  };

  const renderGeneralTab = () => {
    const regimenActual = REGIMENES[config.regimen_principal];
    
    return (
      <div className="mb-4">
        <Card className="mb-4 shadow-sm border-0" style={{ borderRadius: "15px" }}>
          <Card.Header
            className="text-white py-3"
            style={{
              background: `linear-gradient(135deg, ${regimenActual.color} 0%, ${regimenActual.color}dd 100%)`,
              borderRadius: "15px 15px 0 0"
            }}
          >
            <div className="d-flex align-items-center">
              {regimenActual.icono}
              <span className="ms-2 fw-semibold">Régimen Principal de la Empresa</span>
            </div>
          </Card.Header>
          <Card.Body className="p-4">
            <Row className="mb-4">
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                    <FaCog className="me-1" />
                    Régimen Laboral
                  </Form.Label>
                  <Form.Select
                    value={config.regimen_principal}
                    onChange={handleChangeRegimenPrincipal}
                    style={{ borderRadius: "10px" }}
                  >
                    {Object.entries(REGIMENES).map(([key, { titulo }]) => (
                      <option key={key} value={key}>
                        {titulo}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {REGIMENES[config.regimen_principal].descripcion}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4} className="text-end">
                <div
                  className="rounded-3 p-3 text-center"
                  style={{
                    background: `linear-gradient(135deg, ${regimenActual.color} 0%, ${regimenActual.color}dd 100%)`,
                    color: "white",
                  }}
                >
                  {regimenActual.icono}
                  <div className="mt-2 small">Régimen Activo</div>
                </div>
              </Col>
            </Row>

            <Alert 
              variant="info" 
              className="d-flex align-items-start"
              style={{ borderRadius: "15px", border: "none" }}
            >
              <FaInfoCircle className="me-2 mt-1" size={16} />
              <div>
                <strong>{REGIMENES[config.regimen_principal].titulo}:</strong> La
                configuración del régimen principal determinará los cálculos por
                defecto para los nuevos empleados. También puede asignar regímenes
                específicos a nivel individual por empleado.
              </div>
            </Alert>
          </Card.Body>
        </Card>
      </div>
    );
  };

  const renderGratificacionesTab = () => {
    const regimen = config.regimen_principal;
    const regimenInfo = REGIMENES[regimen];

    // Asegurarse de que el objeto gratificacion existe
    if (!config.parametros[regimen]) {
      return (
        <Alert variant="danger" style={{ borderRadius: "15px" }}>
          <FaExclamationTriangle className="me-2" />
          Error: Configuración de régimen no encontrada
        </Alert>
      );
    }

    // Inicializar gratificacion si no existe
    if (!config.parametros[regimen].gratificacion) {
      setConfig((prev) => ({
        ...prev,
        parametros: {
          ...prev.parametros,
          [regimen]: {
            ...prev.parametros[regimen],
            gratificacion: {},
          },
        },
      }));
      return (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: "#8B4513" }} />
          <p className="mt-3 text-muted">Cargando configuración...</p>
        </div>
      );
    }

    const { gratificacion } = config.parametros[regimen];

    return (
      <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
        <Card.Header
          className="text-white py-3"
          style={{
            background: `linear-gradient(135deg, ${regimenInfo.color} 0%, ${regimenInfo.color}dd 100%)`,
            borderRadius: "15px 15px 0 0"
          }}
        >
          <div className="d-flex align-items-center">
            <FaMoneyBillWave className="me-2" />
            <span className="fw-semibold">Configuración de Gratificaciones - {regimenInfo.titulo}</span>
          </div>
        </Card.Header>
        <Card.Body className="p-4">
          {/* Diferentes configuraciones según el régimen */}
          {regimen === "MYPE_MICRO" && (
            <div>
              <Alert 
                variant="warning" 
                className="d-flex align-items-center mb-4"
                style={{ borderRadius: "15px", border: "none" }}
              >
                <FaExclamationTriangle className="me-2" />
                <div><strong>Microempresa:</strong> No están obligadas al pago de gratificaciones</div>
              </Alert>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="aplica-gratificacion"
                  label="Aplicar gratificaciones de manera voluntaria"
                  checked={gratificacion.aplica || false}
                  onChange={(e) =>
                    handleChangeParametro(
                      regimen,
                      "gratificacion",
                      "aplica",
                      e.target.checked
                    )
                  }
                  className="fw-semibold"
                  style={{ color: "#8B4513" }}
                />
              </Form.Group>
              {gratificacion.aplica && (
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                        Factor de gratificación
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          max="1"
                          value={gratificacion.factor || 0}
                          onChange={(e) =>
                            handleChangeParametro(
                              regimen,
                              "gratificacion",
                              "factor",
                              e.target.value
                            )
                          }
                          style={{ borderRadius: "10px 0 0 10px" }}
                        />
                        <InputGroup.Text>sueldo</InputGroup.Text>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        1 = un sueldo completo, 0.5 = medio sueldo
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </div>
          )}

          {regimen === "AGRARIO" && (
            <div>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="incluida-en-diario"
                  label="Gratificación incluida en remuneración diaria"
                  checked={gratificacion.incluida_en_diario || false}
                  onChange={(e) =>
                    handleChangeParametro(
                      regimen,
                      "gratificacion",
                      "incluida_en_diario",
                      e.target.checked
                    )
                  }
                  className="fw-semibold"
                  style={{ color: "#8B4513" }}
                />
                <Form.Text className="text-muted">
                  Según la Ley 31110, las gratificaciones están incluidas en el pago diario
                </Form.Text>
              </Form.Group>
            </div>
          )}

          {regimen === "CAS" && (
            <div>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="aguinaldo"
                  label="Aguinaldo del sector público"
                  checked={gratificacion.aguinaldo || false}
                  onChange={(e) =>
                    handleChangeParametro(
                      regimen,
                      "gratificacion",
                      "aguinaldo",
                      e.target.checked
                    )
                  }
                  className="fw-semibold"
                  style={{ color: "#8B4513" }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="montos-fijos"
                  label="Montos fijos establecidos por MEF"
                  checked={gratificacion.montos_fijos || false}
                  onChange={(e) =>
                    handleChangeParametro(
                      regimen,
                      "gratificacion",
                      "montos_fijos",
                      e.target.checked
                    )
                  }
                  className="fw-semibold"
                  style={{ color: "#8B4513" }}
                />
              </Form.Group>
            </div>
          )}

          {/* Régimen General, Pequeña Empresa y Construcción Civil */}
          {(regimen === "GENERAL" || regimen === "MYPE_PEQUENA" || regimen === "CONSTRUCCION_CIVIL") && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      Factor de gratificación
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        value={gratificacion.factor || 1}
                        onChange={(e) =>
                          handleChangeParametro(
                            regimen,
                            "gratificacion",
                            "factor",
                            e.target.value
                          )
                        }
                        style={{ borderRadius: "10px 0 0 10px" }}
                      />
                      <InputGroup.Text>sueldo</InputGroup.Text>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      1 = un sueldo completo, 0.5 = medio sueldo
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Alert 
                variant="info"
                style={{ borderRadius: "15px", border: "none" }}
              >
                <FaInfoCircle className="me-2" />
                {regimen === "GENERAL" && "Régimen General: 1 sueldo completo en julio y diciembre"}
                {regimen === "MYPE_PEQUENA" && "Pequeña Empresa: 1/2 sueldo en julio y diciembre"}
                {regimen === "CONSTRUCCION_CIVIL" && "Construcción Civil: según convenio colectivo vigente"}
              </Alert>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderCtsTab = () => {
    const regimen = config.regimen_principal;
    const regimenInfo = REGIMENES[regimen];

    // Asegurarse de que el objeto de configuración del régimen existe
    if (!config.parametros[regimen]) {
      return (
        <Alert variant="danger" style={{ borderRadius: "15px" }}>
          <FaExclamationTriangle className="me-2" />
          Error: Configuración de régimen no encontrada
        </Alert>
      );
    }

    // Inicializar cts si no existe
    if (!config.parametros[regimen].cts) {
      setConfig((prev) => ({
        ...prev,
        parametros: {
          ...prev.parametros,
          [regimen]: {
            ...prev.parametros[regimen],
            cts: {},
          },
        },
      }));
      return (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: "#8B4513" }} />
          <p className="mt-3 text-muted">Cargando configuración...</p>
        </div>
      );
    }

    const { cts } = config.parametros[regimen];

    return (
      <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
        <Card.Header
          className="text-white py-3"
          style={{
            background: `linear-gradient(135deg, ${regimenInfo.color} 0%, ${regimenInfo.color}dd 100%)`,
            borderRadius: "15px 15px 0 0"
          }}
        >
          <div className="d-flex align-items-center">
            <BsShieldCheck className="me-2" />
            <span className="fw-semibold">Configuración de CTS - {regimenInfo.titulo}</span>
          </div>
        </Card.Header>
        <Card.Body className="p-4">
          {regimen === "AGRARIO" && (
            <div>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="incluida-en-diario-cts"
                  label="CTS incluida en remuneración diaria"
                  checked={cts.incluida_en_diario || false}
                  onChange={(e) =>
                    handleChangeParametro(
                      regimen,
                      "cts",
                      "incluida_en_diario",
                      e.target.checked
                    )
                  }
                  className="fw-semibold"
                  style={{ color: "#8B4513" }}
                />
                <Form.Text className="text-muted">
                  Según la Ley 31110, la CTS está incluida en el pago diario
                </Form.Text>
              </Form.Group>
            </div>
          )}

          {regimen !== "AGRARIO" && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      Factor CTS
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        value={cts.factor || (regimen === "GENERAL" ? 1 : 0.5)}
                        onChange={(e) =>
                          handleChangeParametro(
                            regimen,
                            "cts",
                            "factor",
                            e.target.value
                          )
                        }
                        style={{ borderRadius: "10px 0 0 10px" }}
                      />
                      <InputGroup.Text>sueldo</InputGroup.Text>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      1 = sueldo completo anual, 0.5 = medio sueldo anual
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Alert 
                variant="info"
                style={{ borderRadius: "15px", border: "none" }}
              >
                <FaInfoCircle className="me-2" />
                {regimen === "GENERAL" && "Régimen General: 1 sueldo anual (pagos en mayo y noviembre)"}
                {regimen === "MYPE_PEQUENA" && "Pequeña Empresa: 15 días de sueldo por año (factor 0.5)"}
                {regimen === "CONSTRUCCION_CIVIL" && "Construcción Civil: porcentaje especial según convenio"}
              </Alert>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderVacacionesTab = () => {
    const regimen = config.regimen_principal;
    const regimenInfo = REGIMENES[regimen];

    // Asegurarse de que el objeto de configuración del régimen existe
    if (!config.parametros[regimen]) {
      return (
        <Alert variant="danger" style={{ borderRadius: "15px" }}>
          <FaExclamationTriangle className="me-2" />
          Error: Configuración de régimen no encontrada
        </Alert>
      );
    }

    // Inicializar vacaciones si no existe
    if (!config.parametros[regimen].vacaciones) {
      setConfig((prev) => ({
        ...prev,
        parametros: {
          ...prev.parametros,
          [regimen]: {
            ...prev.parametros[regimen],
            vacaciones: {},
          },
        },
      }));
      return (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: "#8B4513" }} />
          <p className="mt-3 text-muted">Cargando configuración...</p>
        </div>
      );
    }

    const { vacaciones } = config.parametros[regimen];

    return (
      <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
        <Card.Header
          className="text-white py-3"
          style={{
            background: `linear-gradient(135deg, ${regimenInfo.color} 0%, ${regimenInfo.color}dd 100%)`,
            borderRadius: "15px 15px 0 0"
          }}
        >
          <div className="d-flex align-items-center">
            <FaCalendarAlt className="me-2" />
            <span className="fw-semibold">Configuración de Vacaciones - {regimenInfo.titulo}</span>
          </div>
        </Card.Header>
        <Card.Body className="p-4">
          {regimen === "AGRARIO" && (
            <div>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="escala-progresiva"
                  label="Aplicar escala progresiva"
                  checked={vacaciones.escala_progresiva || false}
                  onChange={(e) =>
                    handleChangeParametro(
                      regimen,
                      "vacaciones",
                      "escala_progresiva",
                      e.target.checked
                    )
                  }
                  className="fw-semibold"
                  style={{ color: "#8B4513" }}
                />
                <Form.Text className="text-muted">
                  Escala progresiva según Ley 31110: de 15 a 30 días según tiempo de servicio
                </Form.Text>
              </Form.Group>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      Días mínimos
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        min="0"
                        max="30"
                        value={vacaciones.dias_min || 15}
                        onChange={(e) =>
                          handleChangeParametro(
                            regimen,
                            "vacaciones",
                            "dias_min",
                            e.target.value
                          )
                        }
                        style={{ borderRadius: "10px 0 0 10px" }}
                      />
                      <InputGroup.Text>días</InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      Días máximos
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        min="15"
                        max="30"
                        value={vacaciones.dias_max || 30}
                        onChange={(e) =>
                          handleChangeParametro(
                            regimen,
                            "vacaciones",
                            "dias_max",
                            e.target.value
                          )
                        }
                        style={{ borderRadius: "10px 0 0 10px" }}
                      />
                      <InputGroup.Text>días</InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}

          {regimen !== "AGRARIO" && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      Días de vacaciones por año
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        min="0"
                        max="30"
                        value={
                          vacaciones.dias ||
                          (regimen === "MYPE_MICRO" || regimen === "MYPE_PEQUENA" ? 15 : 30)
                        }
                        onChange={(e) =>
                          handleChangeParametro(
                            regimen,
                            "vacaciones",
                            "dias",
                            e.target.value
                          )
                        }
                        style={{ borderRadius: "10px 0 0 10px" }}
                      />
                      <InputGroup.Text>días</InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
              <Alert 
                variant="info"
                style={{ borderRadius: "15px", border: "none" }}
              >
                <FaInfoCircle className="me-2" />
                {regimen === "GENERAL" && "Régimen General: 30 días por año"}
                {regimen === "MYPE_MICRO" && "Microempresa: 15 días por año"}
                {regimen === "MYPE_PEQUENA" && "Pequeña Empresa: 15 días por año"}
                {regimen === "CAS" && "Régimen CAS: 30 días por año"}
                {regimen === "CONSTRUCCION_CIVIL" && "Construcción Civil: 30 días por año"}
              </Alert>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderOtrosBeneficiosTab = () => {
    const regimen = config.regimen_principal;
    const regimenInfo = REGIMENES[regimen];

    return (
      <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
        <Card.Header
          className="text-white py-3"
          style={{
            background: `linear-gradient(135deg, ${regimenInfo.color} 0%, ${regimenInfo.color}dd 100%)`,
            borderRadius: "15px 15px 0 0"
          }}
        >
          <div className="d-flex align-items-center">
            <BsAward className="me-2" />
            <span className="fw-semibold">Otros Beneficios - {regimenInfo.titulo}</span>
          </div>
        </Card.Header>
        <Card.Body className="p-4">
          {/* Asignación Familiar */}
          <div className="mb-4">
            <h5 className="mb-3" style={{ color: "#8B4513" }}>
              <FaUsers className="me-2" />
              Asignación Familiar
            </h5>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="aplica-asignacion"
                label="Aplica asignación familiar"
                checked={config.parametros[regimen].asignacion_familiar?.aplica || false}
                onChange={(e) =>
                  handleChangeParametro(
                    regimen,
                    "asignacion_familiar",
                    "aplica",
                    e.target.checked
                  )
                }
                disabled={
                  regimen === "MYPE_MICRO" ||
                  regimen === "MYPE_PEQUENA" ||
                  regimen === "AGRARIO" ||
                  regimen === "CAS"
                }
                className="fw-semibold"
                style={{ color: "#8B4513" }}
              />
            </Form.Group>
            {config.parametros[regimen].asignacion_familiar?.aplica && (
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      Porcentaje de la RMV
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        min="0"
                        max="100"
                        value={
                          config.parametros[regimen].asignacion_familiar?.porcentaje_rmv || 10
                        }
                        onChange={(e) =>
                          handleChangeParametro(
                            regimen,
                            "asignacion_familiar",
                            "porcentaje_rmv",
                            e.target.value
                          )
                        }
                        style={{ borderRadius: "10px 0 0 10px" }}
                      />
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Porcentaje de la Remuneración Mínima Vital (por defecto 10%)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}
          </div>

          {/* ESSALUD especial para régimen agrario */}
          {regimen === "AGRARIO" && (
            <div className="mb-4">
              <h5 className="mb-3" style={{ color: "#8B4513" }}>
                <BsShieldCheck className="me-2" />
                ESSALUD Agrario
              </h5>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      Porcentaje de ESSALUD
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        min="0"
                        max="100"
                        value={config.parametros[regimen].essalud?.porcentaje || 9}
                        onChange={(e) =>
                          handleChangeParametro(
                            regimen,
                            "essalud",
                            "porcentaje",
                            e.target.value
                          )
                        }
                        style={{ borderRadius: "10px 0 0 10px" }}
                      />
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Tasa especial del régimen agrario (6% - 9%)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}

          {/* Indemnización por despido */}
          {regimen !== "CAS" && (
            <div className="mb-4">
              <h5 className="mb-3" style={{ color: "#8B4513" }}>
                <FaExclamationTriangle className="me-2" />
                Indemnización por despido arbitrario
              </h5>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      {regimen === "MYPE_MICRO" || regimen === "MYPE_PEQUENA"
                        ? "Factor por día"
                        : "Factor por año"}
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={
                          config.parametros[regimen].indemnizacion?.factor_por_anio ||
                          (regimen === "GENERAL"
                            ? 1.5
                            : regimen === "MYPE_MICRO"
                            ? 0.33
                            : regimen === "MYPE_PEQUENA"
                            ? 0.66
                            : 0)
                        }
                        onChange={(e) =>
                          handleChangeParametro(
                            regimen,
                            "indemnizacion",
                            "factor_por_anio",
                            e.target.value
                          )
                        }
                        style={{ borderRadius: "10px 0 0 10px" }}
                      />
                      <InputGroup.Text>
                        {regimen === "MYPE_MICRO" || regimen === "MYPE_PEQUENA"
                          ? "días de sueldo"
                          : "sueldos"}
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      {regimen === "MYPE_MICRO" || regimen === "MYPE_PEQUENA"
                        ? "Tope máximo (días)"
                        : "Tope máximo (años)"}
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        min="0"
                        value={
                          regimen === "MYPE_MICRO"
                            ? config.parametros[regimen].indemnizacion?.tope_dias || 90
                            : regimen === "MYPE_PEQUENA"
                            ? config.parametros[regimen].indemnizacion?.tope_dias || 120
                            : config.parametros[regimen].indemnizacion?.tope_anios || 12
                        }
                        onChange={(e) =>
                          handleChangeParametro(
                            regimen,
                            "indemnizacion",
                            regimen === "MYPE_MICRO" || regimen === "MYPE_PEQUENA"
                              ? "tope_dias"
                              : "tope_anios",
                            e.target.value
                          )
                        }
                        style={{ borderRadius: "10px 0 0 10px" }}
                      />
                      <InputGroup.Text>
                        {regimen === "MYPE_MICRO" || regimen === "MYPE_PEQUENA" ? "días" : "años"}
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderConstruccionCivilTab = () => {
    if (config.regimen_principal !== "CONSTRUCCION_CIVIL") {
      return (
        <Alert 
          variant="info"
          className="text-center py-5"
          style={{ borderRadius: "15px", border: "none" }}
        >
          <FaInfoCircle size={48} className="mb-3 opacity-50" />
          <h5>Configuración específica</h5>
          <p>Esta configuración solo aplica al régimen de Construcción Civil</p>
        </Alert>
      );
    }

    const { salarios, bonificaciones } = config.parametros.CONSTRUCCION_CIVIL;
    const regimenInfo = REGIMENES.CONSTRUCCION_CIVIL;

    return (
      <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
        <Card.Header
          className="text-white py-3"
          style={{
            background: `linear-gradient(135deg, ${regimenInfo.color} 0%, ${regimenInfo.color}dd 100%)`,
            borderRadius: "15px 15px 0 0"
          }}
        >
          <div className="d-flex align-items-center">
            <FaHardHat className="me-2" />
            <span className="fw-semibold">Configuración Construcción Civil</span>
          </div>
        </Card.Header>
        <Card.Body className="p-4">
          <div className="mb-4">
            <h5 className="mb-3" style={{ color: "#8B4513" }}>
              <FaUsers className="me-2" />
              Factores Salariales
            </h5>
            <Row className="mb-4">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                    Factor Operario
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      step="0.1"
                      min="0"
                      value={salarios.operario?.factor_jornal || 1}
                      onChange={(e) =>
                        handleChangeParametro(
                          "CONSTRUCCION_CIVIL",
                          "salarios",
                          "operario.factor_jornal",
                          e.target.value
                        )
                      }
                      style={{ borderRadius: "10px 0 0 10px" }}
                    />
                    <InputGroup.Text>× jornal base</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                    Factor Oficial
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      step="0.1"
                      min="0"
                      value={salarios.oficial?.factor_jornal || 0.8}
                      onChange={(e) =>
                        handleChangeParametro(
                          "CONSTRUCCION_CIVIL",
                          "salarios",
                          "oficial.factor_jornal",
                          e.target.value
                        )
                      }
                      style={{ borderRadius: "10px 0 0 10px" }}
                    />
                    <InputGroup.Text>× jornal base</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                    Factor Peón
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      step="0.1"
                      min="0"
                      value={salarios.peon?.factor_jornal || 0.7}
                      onChange={(e) =>
                        handleChangeParametro(
                          "CONSTRUCCION_CIVIL",
                          "salarios",
                          "peon.factor_jornal",
                          e.target.value
                        )
                      }
                      style={{ borderRadius: "10px 0 0 10px" }}
                    />
                    <InputGroup.Text>× jornal base</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="mb-4">
            <h5 className="mb-3" style={{ color: "#8B4513" }}>
              <FaMoneyBillWave className="me-2" />
              Bonificaciones
            </h5>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                    Bonificación por Altura
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      value={bonificaciones.altura?.porcentaje || 7}
                      onChange={(e) =>
                        handleChangeParametro(
                          "CONSTRUCCION_CIVIL",
                          "bonificaciones",
                          "altura.porcentaje",
                          e.target.value
                        )
                      }
                      style={{ borderRadius: "10px 0 0 10px" }}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                  <Form.Text className="text-muted">A partir del 4to piso</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                    Bonificación por Agua
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      value={bonificaciones.agua?.porcentaje || 20}
                      onChange={(e) =>
                        handleChangeParametro(
                          "CONSTRUCCION_CIVIL",
                          "bonificaciones",
                          "agua.porcentaje",
                          e.target.value
                        )
                      }
                      style={{ borderRadius: "10px 0 0 10px" }}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                    Bonif. Altitud mayor 3000 msnm
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      value={bonificaciones.altitud?.mayor_3000msnm || 10}
                      onChange={(e) =>
                        handleChangeParametro(
                          "CONSTRUCCION_CIVIL",
                          "bonificaciones",
                          "altitud.mayor_3000msnm",
                          e.target.value
                        )
                      }
                      style={{ borderRadius: "10px 0 0 10px" }}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                    Bonif. Altitud mayor 4000 msnm
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      value={bonificaciones.altitud?.mayor_4000msnm || 20}
                      onChange={(e) =>
                        handleChangeParametro(
                          "CONSTRUCCION_CIVIL",
                          "bonificaciones",
                          "altitud.mayor_4000msnm",
                          e.target.value
                        )
                      }
                      style={{ borderRadius: "10px 0 0 10px" }}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                    Bonificación por Toxicidad
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      value={bonificaciones.toxicidad?.porcentaje || 10}
                      onChange={(e) =>
                        handleChangeParametro(
                          "CONSTRUCCION_CIVIL",
                          "bonificaciones",
                          "toxicidad.porcentaje",
                          e.target.value
                        )
                      }
                      style={{ borderRadius: "10px 0 0 10px" }}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Card.Body>
      </Card>
    );
  };

  if (loading) {
    return (
      <div
        className="min-vh-100 d-flex justify-content-center align-items-center"
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        }}
      >
        <div className="text-center">
          <Spinner
            animation="border"
            style={{ color: "#8B4513", width: "3rem", height: "3rem" }}
          />
          <p className="mt-3 mb-0" style={{ color: "#8B4513" }}>
            Cargando configuración de regímenes...
          </p>
        </div>
      </div>
    );
  }

  if (!ruc) {
    return (
      <div
        className="min-vh-100"
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
          minHeight: "100vh",
          padding: "20px 0",
        }}
      >
        <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
          <Alert variant="warning" className="text-center">
            <FaExclamationTriangle className="me-2" />
            No se puede cargar la configuración de regímenes laborales sin un RUC válido.
          </Alert>
        </Container>
      </div>
    );
  }

  const estadisticas = calcularEstadisticas();

    return (
      <div
        className="min-vh-100"
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
          minHeight: "100vh",
          width: "100vw",
          maxWidth: "100%",
          padding: "20px 0",
        }}
      >
        <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
          {/* Marco principal */}
          <Card
            className="shadow-lg border-0"
            style={{
              borderRadius: "15px",
              overflow: "hidden",
              minHeight: "90vh",
            }}
          >
            {/* Header Premium */}
            <Card.Header
              className="text-white py-4"
              style={{
                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                borderBottom: "3px solid #D2691E",
              }}
            >
              <div className="text-center">
                <h2 className="fw-bold mb-1">
                  <FaCog className="me-3" />
                  Configuración de Regímenes Laborales
                </h2>
                <p className="mb-0 opacity-75">
                  Configure los parámetros legales según el régimen laboral de su empresa
                </p>
              </div>
            </Card.Header>
  
            <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
              {/* Barra superior con información */}
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="p-4">
                  <Row className="align-items-center">
                    <Col md={1}>
                      <div
                        className="rounded-3 p-3 text-center"
                        style={{
                          background: `linear-gradient(135deg, ${REGIMENES[config.regimen_principal].color} 0%, ${REGIMENES[config.regimen_principal].color}dd 100%)`,
                          color: "white",
                        }}
                      >
                        {REGIMENES[config.regimen_principal].icono}
                      </div>
                    </Col>
                    <Col md={7}>
                      <h5 className="mb-1 fw-semibold">
                        {REGIMENES[config.regimen_principal].titulo}
                      </h5>
                      <p className="text-muted mb-0 small">
                        {REGIMENES[config.regimen_principal].descripcion}
                      </p>
                    </Col>
                    <Col md={4} className="text-end">
                      <div className="d-flex gap-2">
                        <Button
                          onClick={handleResetearConfiguracion}
                          variant="outline-secondary"
                          size="sm"
                          className="px-3"
                          style={{ borderRadius: "10px" }}
                        >
                          <FaUndo className="me-1" />
                          Restablecer
                        </Button>
                        <Button
                          onClick={handleGuardarConfiguracion}
                          disabled={saving}
                          size="lg"
                          className="px-4 py-2"
                          style={{
                            borderRadius: "15px",
                            background: saving 
                              ? "linear-gradient(135deg, #6c757d 0%, #495057 100%)"
                              : "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                            border: "none",
                            fontWeight: "600"
                          }}
                        >
                          {saving ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Guardando...
                            </>
                          ) : (
                            <>
                              <FaSave className="me-2" />
                              Guardar Configuración
                            </>
                          )}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
  
              {/* Estadísticas */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <FaCog size={32} className="mb-2" />
                      <h4 className="fw-bold">{estadisticas.totalRegimenes}</h4>
                      <p className="mb-0">Regímenes Disponibles</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background: `linear-gradient(135deg, ${REGIMENES[config.regimen_principal].color} 0%, ${REGIMENES[config.regimen_principal].color}dd 100%)`,
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      {REGIMENES[config.regimen_principal].icono}
                      <div className="mt-2">
                        <h6 className="fw-bold mb-0">Régimen Activo</h6>
                        <small>{config.regimen_principal}</small>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <BsFileText size={32} className="mb-2" />
                      <h4 className="fw-bold">{estadisticas.parametrosCompletos}</h4>
                      <p className="mb-0">Parámetros Config.</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background: esConfiguracionNueva 
                          ? "linear-gradient(135deg, #ffc107 0%, #e0a800 100%)"
                          : "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      {esConfiguracionNueva ? (
                        <FaExclamationTriangle size={32} className="mb-2" />
                      ) : (
                        <FaCheck size={32} className="mb-2" />
                      )}
                      <h6 className="fw-bold mb-0">
                        {esConfiguracionNueva ? "Nueva Config." : "Guardada"}
                      </h6>
                      {lastSaved && (
                        <small>
                          {lastSaved.toLocaleDateString()}
                        </small>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
  
              {/* Tabs de configuración */}
              <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="nav-tabs-custom"
                  style={{
                    borderBottom: "none",
                    paddingLeft: "20px",
                    paddingRight: "20px",
                    paddingTop: "20px"
                  }}
                >
                  <Tab 
                    eventKey="general" 
                    title={
                      <span>
                        <FaCog className="me-2" />
                        General
                      </span>
                    }
                  >
                    <div className="p-3">
                      {renderGeneralTab()}
                    </div>
                  </Tab>
  
                  <Tab 
                    eventKey="gratificaciones" 
                    title={
                      <span>
                        <FaMoneyBillWave className="me-2" />
                        Gratificaciones
                      </span>
                    }
                  >
                    <div className="p-3">
                      {renderGratificacionesTab()}
                    </div>
                  </Tab>
  
                  <Tab 
                    eventKey="cts" 
                    title={
                      <span>
                        <BsShieldCheck className="me-2" />
                        CTS
                      </span>
                    }
                  >
                    <div className="p-3">
                      {renderCtsTab()}
                    </div>
                  </Tab>
  
                  <Tab 
                    eventKey="vacaciones" 
                    title={
                      <span>
                        <FaCalendarAlt className="me-2" />
                        Vacaciones
                      </span>
                    }
                  >
                    <div className="p-3">
                      {renderVacacionesTab()}
                    </div>
                  </Tab>
  
                  <Tab 
                    eventKey="beneficios" 
                    title={
                      <span>
                        <BsAward className="me-2" />
                        Otros Beneficios
                      </span>
                    }
                  >
                    <div className="p-3">
                      {renderOtrosBeneficiosTab()}
                    </div>
                  </Tab>
  
                  <Tab 
                    eventKey="construccion" 
                    title={
                      <span>
                        <FaHardHat className="me-2" />
                        Construcción Civil
                      </span>
                    }
                    disabled={config.regimen_principal !== "CONSTRUCCION_CIVIL"}
                  >
                    <div className="p-3">
                      {renderConstruccionCivilTab()}
                    </div>
                  </Tab>
                </Tabs>
              </Card>
  
              {/* Información legal al pie */}
              <Card 
                className="mt-4 border-0 shadow-sm"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="p-3">
                  <Alert 
                    variant="info" 
                    className="mb-0 d-flex align-items-start"
                    style={{ borderRadius: "15px", border: "none" }}
                  >
                    <FaInfoCircle className="me-2 mt-1" size={16} />
                    <div>
                      <strong>Información Legal:</strong> Esta configuración determina los cálculos de planilla según la legislación laboral peruana vigente. 
                      Los cambios afectarán a todos los nuevos empleados registrados. Para modificar el régimen de empleados existentes, 
                      edítelos individualmente desde el módulo de empleados.
                      {lastSaved && (
                        <div className="mt-2 small text-muted">
                          <strong>Última actualización:</strong> {lastSaved.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </Alert>
                </Card.Body>
              </Card>
            </Card.Body>
          </Card>
        </Container>
  
        <style jsx>{`
          .nav-tabs-custom .nav-link {
            border: none !important;
            border-radius: 10px !important;
            margin-right: 8px !important;
            color: #8B4513 !important;
            font-weight: 600 !important;
            padding: 12px 20px !important;
            background: transparent !important;
            transition: all 0.3s ease !important;
          }
          
          .nav-tabs-custom .nav-link:hover {
            background: rgba(139, 69, 19, 0.1) !important;
            color: #8B4513 !important;
          }
          
          .nav-tabs-custom .nav-link.active {
            background: linear-gradient(135deg, #8B4513 0%, #A0522D 100%) !important;
            color: white !important;
            border: none !important;
          }
          
          .nav-tabs-custom .nav-link.disabled {
            opacity: 0.5 !important;
            cursor: not-allowed !important;
          }
          
          .nav-tabs-custom {
            border-bottom: 2px solid #f1f3f4 !important;
          }
        `}</style>
      </div>
    );
  };
  
  export default ConfiguracionRegimen;